import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Star, Users, Sparkles } from 'lucide-react';
import TopExperienceLayer from '../components/top/TopExperienceLayer';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const ComingSoonPage = ({ feature = 'reviews' }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const featureConfig = {
    reviews: {
      title: 'College Reviews',
      subtitle: 'Student voices coming soon',
      description: 'Get authentic insights from current students and alumni. Read verified reviews about academics, campus life, social scene, and more.',
      icon: Star,
      iconColor: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      benefits: [
        'Verified student reviews',
        'Honest academic insights',
        'Campus culture perspectives',
        'Real student experiences'
      ]
    },
    events: {
      title: 'Campus Events',
      subtitle: 'Campus activity coming soon',
      description: 'Stay connected with campus happenings. Discover open houses, virtual tours, information sessions, and other important events.',
      icon: Calendar,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      benefits: [
        'Virtual campus tours',
        'Information sessions',
        'Open house dates',
        'Application deadlines'
      ]
    }
  };

  const config = featureConfig[feature] || featureConfig.reviews;
  const Icon = config.icon;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <TopExperienceLayer />
      
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(id ? `/colleges/${id}` : -1)}
            className="mb-8 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to College Profile
          </Button>

          {/* Coming Soon Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 sm:p-12 text-center">
            {/* Icon */}
            <div className={`inline-flex items-center justify-center w-20 h-20 ${config.bgColor} ${config.borderColor} border-2 rounded-full mb-6`}>
              <Icon size={40} className={config.iconColor} />
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              {config.title}
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {config.subtitle}
            </p>

            {/* Description */}
            <p className="text-base text-gray-700 max-w-2xl mx-auto mb-8 leading-relaxed">
              {config.description}
            </p>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-10">
              {config.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg text-left"
                >
                  <div className="flex-shrink-0">
                    <Sparkles size={20} className="text-[#1a5d3a]" />
                  </div>
                  <span className="text-sm text-gray-700 font-medium">
                    {benefit}
                  </span>
                </div>
              ))}
            </div>

            {/* Launch Message */}
            <div className={`${config.bgColor} ${config.borderColor} border-2 rounded-xl p-6 mb-8`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Users size={20} className={config.iconColor} />
                <span className="text-sm font-semibold text-gray-900">
                  Launching Soon
                </span>
              </div>
              <p className="text-sm text-gray-600">
                We&apos;re working hard to bring you this feature. Check back soon for updates!
              </p>
            </div>

            {/* Action Button */}
            <Button
              onClick={() => navigate(id ? `/colleges/${id}` : -1)}
              className="bg-[#1a5d3a] hover:bg-[#15472d] text-white px-8 py-3 text-base"
            >
              Return to College Profile
            </Button>
          </div>

          {/* Footer Info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Want to be notified when this feature launches?{' '}
              <button className="text-[#1a5d3a] font-medium hover:underline">
                Sign up for updates
              </button>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ComingSoonPage;
